/**
 * Review notes on a ticket or job (Jobs & Tickets board, Gareth 2026-10-20).
 *
 * Team leaders, managers and admins add a short note while reviewing work on
 * the board. Each note is a customer timeline entry linked to the ticket or
 * job, so it also shows here on the ticket or job page's History tab and on
 * the customer timeline. Coordinators and everyone else read them.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/format';
import './ReviewNotes.css';

export const REVIEW_NOTE_MAX = 2000;

/** The board's work_type for each kind of record the notes route knows. */
export const NOTE_TYPE_FOR_WORK = {
  reactive_ticket: 'ticket',
  scheduled_work: 'scheduled_task',
  scheduled_maintenance: 'maintenance_task'
};

function ReviewNotes({ type, id, heading = 'Review notes', onAdded }) {
  const { authFetch } = useAuth();
  const [notes, setNotes] = useState(null);
  const [canAdd, setCanAdd] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await authFetch(`/api/v1/work-board/${type}/${id}/notes`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The notes could not be read.');
      setNotes(data.data || []);
      setCanAdd(data.can_add_notes === true);
    } catch (err) {
      setNotes([]);
      setError(err.message === 'Failed to fetch' ? 'The notes could not be read. Check your signal.' : err.message);
    }
  }, [authFetch, type, id]);

  useEffect(() => { setNotes(null); setText(''); load(); }, [load]);

  const add = async () => {
    const body = text.trim();
    if (!body) return;
    setSaving(true);
    setError('');
    try {
      const response = await authFetch(`/api/v1/work-board/${type}/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The note could not be saved.');
      setText('');
      await load();
      if (typeof onAdded === 'function') onAdded();
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'The note could not be sent. Check your signal and try again.' : err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="review-notes">
      {heading && <h3 className="review-notes-heading">{heading}</h3>}
      {canAdd && (
        <div className="review-notes-add">
          <textarea
            value={text}
            maxLength={REVIEW_NOTE_MAX}
            rows={2}
            placeholder="Add a review note…"
            aria-label="Add a review note"
            onChange={(e) => setText(e.target.value)}
          />
          <div className="review-notes-actions">
            <span className="review-notes-muted">{text.length} / {REVIEW_NOTE_MAX}</span>
            <button type="button" className="btn-primary btn-small" disabled={saving || !text.trim()} onClick={add}>
              {saving ? 'Saving…' : 'Add Note'}
            </button>
          </div>
        </div>
      )}
      {error && <p className="review-notes-error" role="alert">{error}</p>}
      {notes === null ? (
        <p className="review-notes-muted">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="review-notes-muted">No review notes yet.</p>
      ) : (
        <ul className="review-notes-list">
          {notes.map(note => (
            <li key={note.entry_id}>
              <div className="review-notes-meta">
                <strong>{note.user_name || 'Someone'}</strong>
                {note.subject === 'Planning note' && <span className="review-notes-tag">Ops Planning</span>}
                <span>{formatDateTime(note.occurred_at)}</span>
              </div>
              <p>{note.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ReviewNotes;
